<template>
  <div class="report-content">
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center py-4 h-100">
      <v-progress-circular color="primary" indeterminate></v-progress-circular>
    </div>

    <div v-else-if="!selectedColumns.length" class="w-100 h-100 d-flex justify-content-center align-items-center">
      <i class="ma-0">Check the columns on the left to include them in the preview and template.</i>
    </div>

    <div class="" v-else>

      <ColumnFilter v-if="reportTemplate && Object.keys(reportTemplate).length" />

      <div class="record-count d-flex justify-end px-4">
        <h5 class="ma-0">Total <b>{{ formattedDataCount }}</b> data based on the applied filters.</h5>
      </div>

      <v-divider class="ma-0 mt-4 mx-4" color="#D7D7D7"></v-divider>

      <div class="column-title my-4 mx-4">
        <h4 class="ma-0" style="font-size: 16px;"><b>Column Preview</b></h4>
        <div class="d-flex align-items-center justify-space-between mt-2 mb-4">
          <ColumnPagination />
          <button class="btn btn-outline-primary d-flex align-items-center" @click="runReport">
            <v-icon small color="#007BFF" class="mr-1">mdi-play-circle</v-icon>
            Run Report
          </button>
        </div>
      </div>
      <div class="nav-container">
        <button class="btn-vertical prev" :class="{ 'btn-vertical-primary': fieldPagination.current_page > 1 }"
          @click="goToPage(fieldPagination.current_page - 1)">
          <v-icon :color="fieldPagination.current_page > 1 ? '#FFF' : '#D7D7D7'">mdi-chevron-left</v-icon>
        </button>
        <button class="btn-vertical next"
          :class="{ 'btn-vertical-primary': fieldPagination.current_page < fieldPagination.total_pages }"
          @click="goToPage(fieldPagination.current_page + 1)">
          <v-icon
            :color="fieldPagination.current_page < fieldPagination.total_pages ? '#FFF' : '#D7D7D7'">mdi-chevron-right</v-icon>
        </button>
      </div>
      <div class="column-container" :style="{ marginTop: isShowLegend ? '108px' : '' }">
        <div class="position-relative" style="width: fit-content;">
          <div class="d-flex">
            <div v-for="(column, index) in processedColumns" :key="index" class="column-item">
              <div class="column-card">
                <div class="column-header" :class="{ 'data-dummy': column.isDummy }">
                  <v-tooltip v-if="!column.isDummy" bottom content-class="tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <h4 v-on="on" class="ma-0"
                        style="font-size: 16px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                        <b>{{ getColumnLabel(column) }}</b>
                      </h4>
                    </template>
                    <span style="background-color: white;">
                      <p class="ma-0">{{ getColumnLabel(column) }}</p>
                      <i style="color: #848484; font-size: 12px;">
                        <b>Original Column Name :</b>
                        {{ column.original_header_name }}
                      </i>
                    </span>
                  </v-tooltip>
                  <div v-else class="d-flex">
                    <v-icon small color="#3F3F3F" class="mr-2"
                      style="padding: 2.5px 0;">mdi-file-document-edit-outline</v-icon>
                    <h4 class="ma-0"
                      style="font-size: 16px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                      <b>{{ getColumnLabel(column) }}</b>
                    </h4>
                  </div>
                  <v-menu v-if="!column.isDummy" min-width="120px" bottom close-on-click nudge-bottom="24px">
                    <template v-slot:activator="{ on, attrs }">
                      <div role="button" v-bind="attrs" v-on="on">
                        <v-icon small color="#3F3F3F" dense>mdi-dots-horizontal</v-icon>
                      </div>
                    </template>
                    <div class="bg-white px-3 py-2 d-flex flex-column" style="gap: 8px; color: #3F3F3F;">
                      <div role="button" @click="openModal(column)">Rename</div>
                      <!-- <div role="button" :disabled="!showPreview[column.columnName]">
                        <span>Sort <span class="text-primary">A</span> <v-icon small color="#3F3F3F"
                            dense>mdi-arrow-right</v-icon> <span class="text-danger">Z</span></span>
                      </div>
                      <div role="button" :disabled="!showPreview[column.columnName]">
                        <span>Sort <span class="text-danger">Z</span> <v-icon small color="#3F3F3F"
                            dense>mdi-arrow-right</v-icon> <span class="text-primary">A</span></span>
                      </div> -->
                      <div role="button" class="text-danger" @click="removeColumn(column)">Remove</div>
                    </div>
                  </v-menu>
                </div>
                <div class="content-container" :style="{ backgroundColor: column.isDummy ? '#F4F4F4' : '#FFFFFF' }">
                  <div v-if="column.isQuestionnaire">
                    <template v-if="column.previewData.length">
                      <div class="content-preview" v-for="(item, index) in column.previewData" :key="index">
                        <h4 class="ma-0" style="font-size: 16px;">
                          <span v-if="item || item === 0">{{ item }}</span>
                          <i v-else class="text-muted" style="color: #DDDDDD !important;">Empty</i>
                        </h4>
                      </div>
                    </template>
                    <div v-else>
                      <i class="text-muted text-center d-block" style="color: #BDBDBD !important;">Preview unavailable.
                        Ensure your
                        organization has data to display.</i>
                    </div>
                  </div>
                  <div class="d-flex justify-content-center align-items-start position-relative"
                    style="height: 802px; color: #007BFF;"
                    v-else-if="!showPreview[`${column.category_key || column.group}_${column.columnName}`]">
                    <button @click="togglePreview(column)" class="d-flex align-items-center"
                      style="position: sticky; top: 60%; transform: translateY(-50%);">
                      <v-icon small color="#007BFF" class="mr-2" style="height: fit-content;">mdi-eye-outline</v-icon>
                      Show Preview Data
                    </button>
                  </div>
                  <div v-if="showPreview[`${column.category_key || column.group}_${column.columnName}`]">
                    <template
                      v-if="previewData[`${column.category_key || column.group}_${column.columnName}`] && previewData[`${column.category_key || column.group}_${column.columnName}`].length > 0">
                      <div class="content-preview"
                        v-for="(item, index) in previewData[`${column.category_key || column.group}_${column.columnName}`]"
                        :key="index">
                        <h4 class="ma-0" style="font-size: 16px;">
                          <span v-if="item || item === 0">
                            <span v-if="column.columnName === 'prevailing_wage_cents'">{{ formatCurrency(item) }}</span>
                            <span v-else>{{ item }}</span>
                          </span>
                          <i v-else class="text-muted" style="color: #DDDDDD !important;">Empty</i>
                        </h4>
                      </div>
                    </template>
                    <div v-else>
                      <i class="text-muted text-center d-block" style="color: #BDBDBD !important;">Preview unavailable.
                        Ensure your
                        organization has data to display.</i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <RenameColumnModal v-if="showModal" :dialog="showModal" @toggle-modal="toggleModal" :dataColumn="data" />
  </div>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from 'vuex';

import RenameColumnModal from './RenameColumnModal.vue';
import ColumnPagination from './ColumnPagination.vue'
import ColumnFilter from './ColumnFilter.vue';

export default {
  components: {
    RenameColumnModal,
    ColumnPagination,
    ColumnFilter,
  },
  props: {

  },
  computed: {
    ...mapGetters({
      selectedColumns: 'reportTemplates/selectedColumns',
      fieldPagination: 'reportTemplates/fieldPagination',
      originalColumns: 'reportTemplates/originalColumns',
      reportTemplate: 'reportTemplates/reportTemplate',
      previewDataIds: 'reportTemplates/previewDataIds',
      isLoading: 'reportTemplates/isLoading',
      dataCount: 'reportTemplates/dataCount',
      hasUnsavedChanges: "reportTemplates/hasUnsavedChanges",
      unsavedChanges: "reportTemplates/unsavedChanges",
    }),
    paginatedColumns() {
      const startIndex = (this.fieldPagination.current_page - 1) * this.fieldPagination.per_page;
      const endIndex = this.fieldPagination.current_page * this.fieldPagination.per_page;
      return this.selectedColumns.slice(startIndex, endIndex);
    },
    processedColumns() {
      return this.sortedColumns.flatMap(column => {
        if (column.data && Array.isArray(column.data)) {
          return column.data.map(item => ({
            ...item,
            parentGroup: column.group,
            position: column.position,
            isDummy: true,
            isQuestionnaire: column.isQuestionnaire || false,
            previewData: item.previewData || []
          }));
        }
        return {
          ...column,
          isDummy: false,
          isQuestionnaire: column.isQuestionnaire || false,
        };
      }).sort((a, b) => a.position - b.position);
    },
    isShowLegend() {
      return this.reportTemplate.type === 'Report::Ticket';
    },
    formattedDataCount() {
      if (this.dataCount == null) return '-';
      return this.dataCount.toLocaleString('en-US');
    }
  },
  data() {
    return {
      showModal: false,
      data: null,
      showPreview: {},
      previewData: {},
      sortedColumns: [],
      isMenuOpen: false,
    }
  },
  methods: {
    ...mapActions({
      getReportFields: 'reportTemplates/getReportFields',
    }),
    ...mapMutations({
      setPagination: 'reportTemplates/setFieldPagination',
    }),
    openModal(data) {
      this.showModal = true
      this.data = { ...data }
    },
    toggleModal(payload) {
      this.showModal = payload
    },
    toggleDrawer() {
      this.$emit('setDrawerShow', true);
    },
    async togglePreview(column) {
      const uniqueKey = `${column.category_key || column.group}_${column.column_name || column.columnName}`;
      const templateId = this.reportTemplate?.id;
      const fieldId = column.id

      try {
        const previewData = await this.$store.dispatch('reportTemplates/getColumnPreview', {
          templateId,
          fieldId,
          column,
          previewDataIds: this.previewDataIds
        });

        if (previewData) {
          this.$set(this.previewData, uniqueKey, previewData);
          this.$set(this.showPreview, uniqueKey, true);
        }
      } catch (error) {
        console.error("Error in togglePreview:", error);
      }
    },
    async fetchPreviewData(column) {
      try {
        const previewData = await this.$store.dispatch('reportTemplates/getColumnPreview', {
          templateId: this.$route.params.id,
          fieldId: null,
          column: {
            columnName: column.columnName,
            group: 'Questionnaire::Template',
          },
          previewDataIds: this.previewDataIds || [],
        });

        return previewData.map(item => ({
          groupId: `questionnaire_${column.columnName}`,
          columnName: item.header.toLowerCase().replace(/\s+/g, '_'),
          header_name: item.header,
          original_header_name: item.header,
          previewData: item.data || [],
        }));
      } catch (error) {
        console.error("Failed to fetch questionnaire template preview:", error);
        return [];
      }
    },
    removeColumn(column) {
      const originalIndex = this.originalColumns.findIndex(orig => orig.columnName === column.columnName && orig.group === column.group);

      if (originalIndex !== -1) {
        this.originalColumns[originalIndex]._destroy = 1;
      }

      const updatedColumns = this.selectedColumns.filter(
        col => !(col.group === column.group && col.columnName === column.columnName)
      );

      const perPage = this.fieldPagination.per_page;
      const totalCount = updatedColumns.length;
      const totalPages = Math.ceil(totalCount / perPage);

      let currentPage = this.fieldPagination.current_page;

      if (currentPage > totalPages) {
        currentPage = totalPages;
      }

      this.$store.commit('reportTemplates/setSelectedColumns', updatedColumns);
      this.$store.commit('reportTemplates/setUnsavedChanges', true);
      this.$store.commit('reportTemplates/setFieldPagination', {
        current_page: currentPage,
        per_page: perPage,
        total_count: totalCount,
        total_pages: totalPages,
        count: perPage,
      });
    },
    goToPage(page) {
      if (page < 1 || page > this.fieldPagination.total_pages) return;

      if (this.hasUnsavedChanges) {
        this.setPagination({
          current_page: page,
          per_page: this.fieldPagination.per_page,
          total_count: this.selectedColumns.length,
          total_pages: Math.ceil(this.selectedColumns.length / this.fieldPagination.per_page),
        });
      } else {
        this.setPagination({
          current_page: page,
          per_page: this.fieldPagination.per_page,
        });

        this.getReportFields(this.$route.params.id);
      }
    },
    runReport() {
      if (this.hasUnsavedChanges) {
        this.$store.commit('reportTemplates/setConfirmShow', {
          isShow: true,
          type: 'run',
          title: `Run <b>${this.reportTemplate.name}</b> Report`,
          description: "You've made some changes to this report. Want to update them before running?",
        });
      } else {
        this.$store.dispatch('reportTemplates/exportReport', this.reportTemplate?.id);
      }
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value / 100);
    },
    getColumnLabel(column) {
      const name = column.header_name || column.original_header_name;
      if (this.reportTemplate.type !== 'Report::Client' && ['Business Unit', 'Department'].includes(name)) {
        return `${name} (Client)`;
      }
      return name;
    },
  },
  watch: {
    paginatedColumns: {
      immediate: true,
      deep: true,
      async handler(newColumns) {
        this.sortedColumns = await Promise.all(
          newColumns.map(async (column) => {
            if (column.group === 'Questionnaire::Template' && column.id) {
              if (!column.data || column.data.length === 0) {
                column.data = await this.fetchPreviewData(column);
              }
            }
            return column;
          })
        );
      }
    },
    previewDataIds: {
      handler(newVal) {
        if (newVal) {
          this.showPreview = {};
          this.previewData = {};
        }
      },
    }
  },
  created() {
    if (this.$route.params.id) {
      this.$store.dispatch('reportTemplates/getPreviewDataIds', { id: this.$route.params.id });
      this.$store.dispatch('reportTemplates/getDataCount', { id: this.$route.params.id });
    }
  }
}
</script>

<style lang="scss" scoped>
.report-content {
  width: 100%;
  overflow-x: auto;
  color: #3F3F3F;
  position: relative;
  padding-right: 8px;


  .index {
    width: fit-content;
    background: white;
    border: 1px solid #BDBDBD;
    border-radius: 4px;
    padding: 8px;
  }

  .filter {
    width: 120px;
    background: white;
    border: 1px solid #BDBDBD;
    border-radius: 4px;
    padding: 8px;
  }

  .column-container {
    overflow: auto;
    height: calc(100vh - 500px);
    padding: 0 16px;
    position: relative;
  }

  .nav-container {
    display: flex;
    justify-content: space-between;
    width: 100%;
    height: fit-content !important;
    position: absolute;
    top: 70%;
    pointer-events: none;
  }

  .btn-vertical {
    position: sticky;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    background: white;
    border: 1px solid #D7D7D7;
    border-radius: 12px 14px;
    padding: 6px 8px 8px 8px;
    cursor: pointer;
    box-shadow: 0px 3px 4px 0px #00000026;
    pointer-events: initial;
  }

  .btn-vertical.prev {
    left: 2px;
  }

  .btn-vertical.next {
    right: 14px;
  }

  .btn-vertical-primary {
    background-color: #007BFF;
    color: white !important;
  }

  .column-card {
    min-width: 216px;
    width: 216px;
    border: 1px solid #D7D7D7;
    border-radius: 8px;
  }

  .column-header {
    background-color: #EDF4FE;
    padding: 10px 16px;
    display: flex;
    gap: 6px;
    align-items: center;
    justify-content: space-between;
    border-radius: 8px 8px 0 0;
  }

  .data-dummy {
    background-color: #D7D7D7 !important;
  }

  .content-container {
    background-color: #FFFFFF;
    height: auto;
  }

  .content-preview {
    padding: 10px 16px;
    border-bottom: 1px solid #D7D7D7;
  }

  .content-preview h4 {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

}

.custom-tooltip {
  opacity: 1 !important;
}

::v-deep .v-tooltip {
  z-index: 99 !important;
}
</style>